import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { submitSurvey } from "../../../apis/user-apis/SurveyApis";

const RANK_ITEMS = [
  { key: "door", label: "A neighbor or volunteer knocking on your door" },
  { key: "phone", label: "A phone call or text message" },
  { key: "literature", label: "A brochure left at your door" },
  { key: "digital", label: "Campaign website or social media" },
  { key: "mail", label: "Information mailed to your home" },
  { key: "events", label: "In-person meet-and-greet events" },
];

const AGE_GROUPS = ["18-25", "26-35", "36-45", "46-55", "55+"];

const AGE_LABELS = {
  "18-25": "18–25",
  "26-35": "26–35",
  "36-45": "36–45",
  "46-55": "46–55",
  "55+": "55 and above",
};

const emptyRankings = () =>
  RANK_ITEMS.reduce((acc, item) => ({ ...acc, [item.key]: "" }), {});

const SurveyForm = () => {
  const [rankings, setRankings] = useState(emptyRankings());
  const [ageGroup, setAgeGroup] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", community: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const rankOwner = useMemo(() => {
    const owner = {};
    RANK_ITEMS.forEach(({ key, label }) => {
      const v = rankings[key];
      if (v) owner[v] = label;
    });
    return owner;
  }, [rankings]);

  const setRank = (key, value) => {
    setRankings((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, rankings: undefined }));
  };

  const validate = () => {
    const next = {};
    const values = RANK_ITEMS.map((i) => rankings[i.key]);
    if (values.some((v) => v === "")) {
      next.rankings = "Please rank all six options.";
    } else if (new Set(values).size !== values.length) {
      next.rankings = "Each rank 1–6 can only be used once.";
    }
    if (!ageGroup) next.ageGroup = "Please select an age group.";
    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) {
      next.email = "Enter a valid email or leave it blank.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    const payload = {
      rankings: RANK_ITEMS.reduce(
        (acc, item) => ({ ...acc, [item.key]: Number(rankings[item.key]) }),
        {}
      ),
      ageGroup,
      name: contact.name.trim() || undefined,
      email: contact.email.trim() || undefined,
      community: contact.community.trim() || undefined,
    };

    try {
      setSubmitting(true);
      await submitSurvey(payload);
      setSubmitted(true);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "We could not reach the server just now. Please try again in a moment.";
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: { xs: 5, md: 6 },
          px: 3,
          bgcolor: "#fff",
          border: "1px solid #B7DCC4",
          borderRadius: 2,
          boxShadow: "0 4px 16px rgba(47,111,78,0.08)",
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "#2F6F4E" }} />
        <Typography
          variant="h5"
          sx={{
            fontFamily: "'Playfair Display', serif",
            color: "#072844",
            fontWeight: 700,
            mt: 1.5,
          }}
        >
          Thanks for taking the survey!
        </Typography>
        <Typography variant="body2" sx={{ color: "#5b5648", mt: 1, maxWidth: 460, mx: "auto" }}>
          Your response helps Gary's team reach District 8 neighbors the
          right way. We appreciate you taking the time.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" },
          gridTemplateRows: { md: "1fr" },
          columnGap: { xs: 0, md: 2.5 },
          rowGap: { xs: 2, md: 0 },
        }}
      >
        {/* Left column — Q1 Rankings */}
        <Box
          sx={{
            display: "grid",
            gridTemplateRows: "1fr",
            minWidth: 0,
          }}
        >
          <Card
            sx={{
              mb: 0,
              p: { xs: 2, sm: 2.5 },
              display: "flex",
              flexDirection: "column",
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <QuestionHeader
              title="1. How would you prefer to learn about Gary?"
              hint="Rank each option from 1 (most preferred) to 6 (least preferred)."
            />
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              {RANK_ITEMS.map(({ key, label }) => (
                <RankRow
                  key={key}
                  label={label}
                  value={rankings[key]}
                  onChange={(v) => setRank(key, v)}
                  rankOwner={rankOwner}
                  itemLabel={label}
                />
              ))}
            </Box>
            {errors.rankings && (
              <FormHelperText error sx={{ mt: 1, fontSize: "0.82rem" }}>
                {errors.rankings}
              </FormHelperText>
            )}
          </Card>
        </Box>

        {/* Right column — Q2 (age) stacked above Q3 (contact) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateRows: { md: "auto 1fr" },
            gap: { xs: 2, sm: 2.5 },
            minWidth: 0,
          }}
        >
          <Card sx={{ mb: 0, p: { xs: 2, sm: 2.5 } }}>
              <QuestionHeader
                title="2. What's your age group?"
                hint="Helps us tailor how we communicate with different generations."
              />
              <FormControl error={!!errors.ageGroup} component="fieldset">
                <RadioGroup
                  row
                  value={ageGroup}
                  onChange={(e) => {
                    setAgeGroup(e.target.value);
                    setErrors((prev) => ({ ...prev, ageGroup: undefined }));
                  }}
                  sx={{ gap: { xs: 0.75, md: 1 }, flexWrap: "wrap", rowGap: { xs: 0.75, md: 1 } }}
                >
                  {AGE_GROUPS.map((g) => (
                    <FormControlLabel
                      key={g}
                      value={g}
                      control={
                        <Radio
                          size="small"
                          sx={{ color: "#0B3D6E", p: 0.5, ml: 0.5, mr: 0.5 }}
                        />
                      }
                      label={AGE_LABELS[g]}
                      sx={{
                        m: 0,
                        border: "1px solid",
                        borderColor:
                          ageGroup === g ? "#0B3D6E" : "#D9CBA8",
                        borderRadius: 1,
                        pr: { xs: 1, md: 1.5 },
                        pl: 0.5,
                        py: 0.35,
                        minHeight: { xs: 32, md: 36 },
                        flexGrow: 1,
                        flexBasis: { xs: "45%", sm: "28%", md: "auto" },
                        justifyContent: "flex-start",
                        bgcolor:
                          ageGroup === g ? "#EAF0F7" : "#FAF5E9",
                        transition:
                          "background 0.15s ease, border-color 0.15s ease",
                        "& .MuiTypography-root": {
                          fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                          whiteSpace: "nowrap",
                        },
                        "&:hover": {
                          borderColor:
                            ageGroup === g ? "#0B3D6E" : "#B8892B",
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
                {errors.ageGroup && (
                  <FormHelperText>{errors.ageGroup}</FormHelperText>
                )}
              </FormControl>
            </Card>

          <Card
            sx={{
              mb: 0,
              p: { xs: 2, sm: 2.5 },
              display: "flex",
              flexDirection: "column",
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <QuestionHeader
              title="3. Stay in touch"
              hint="Leave your info if you'd like updates from the campaign."
              optional
            />
            <Stack spacing={2}>
              <TextField
                label="Name (optional)"
                size="small"
                fullWidth
                value={contact.name}
                onChange={(e) =>
                  setContact({ ...contact, name: e.target.value })
                }
                placeholder="Jane Smith"
              />
              <TextField
                label="Email (optional)"
                size="small"
                fullWidth
                type="email"
                value={contact.email}
                onChange={(e) => {
                  setContact({ ...contact, email: e.target.value });
                  setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                placeholder="jane@example.com"
                error={!!errors.email}
                helperText={errors.email || ""}
              />
              <TextField
                label="Community or neighborhood (optional)"
                size="small"
                fullWidth
                value={contact.community}
                onChange={(e) =>
                  setContact({ ...contact, community: e.target.value })
                }
                placeholder="e.g. Westbrook, Silver Lake Estates"
              />
            </Stack>
          </Card>
        </Box>
      </Box>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {serverError}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={submitting}
        sx={{
          mt: { xs: 3, md: 4 },
          bgcolor: "#0B3D6E",
          color: "#fff",
          py: 1.5,
          fontWeight: 700,
          fontSize: "1rem",
          textTransform: "none",
          borderRadius: 1,
          boxShadow: "0 4px 12px rgba(11,61,110,0.25)",
          "&:hover": {
            bgcolor: "#072844",
            boxShadow: "0 6px 16px rgba(11,61,110,0.35)",
          },
        }}
      >
        {submitting ? (
          <>
            <CircularProgress size={18} sx={{ color: "#fff", mr: 1.5 }} />
            Submitting…
          </>
        ) : (
          "Submit Survey"
        )}
      </Button>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: "#8a8370",
          mt: 1.5,
        }}
      >
        Paid for by Friends of Gary Taylor. This survey does not collect or
        share your voting history.
      </Typography>
    </Box>
  );
};

const Card = ({ children, sx }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      border: "1px solid #D9CBA8",
      borderRadius: 1,
      p: { xs: 2, sm: 2.5 },
      mb: 2,
      boxShadow: "0 1px 3px rgba(11,61,110,0.06)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const QuestionHeader = ({ title, hint, optional }) => (
  <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
    <Typography
      variant="subtitle1"
      sx={{
        color: "#072844",
        fontWeight: 700,
        fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
        letterSpacing: 0.2,
      }}
    >
      {title}
      {optional && (
        <Typography
          component="span"
          sx={{
            color: "#8a8370",
            fontSize: { xs: "0.7rem", md: "0.78rem" },
            ml: 0.75,
            fontWeight: 400,
          }}
        >
          (optional)
        </Typography>
      )}
    </Typography>
    {hint && (
      <Typography variant="body2" sx={{ color: "#5b5648", mt: 0.25, fontSize: { xs: "0.78rem", md: "0.875rem" } }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const RankRow = ({ label, value, onChange, rankOwner, itemLabel }) => {
  const rankOptions = [1, 2, 3, 4, 5, 6];
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0.5,
        "@media (min-width: 1280px)": {
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
        },
        py: 1.25,
        borderBottom: "1px solid #EFE7D3",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          flex: 1,
          minWidth: 0,
          lineHeight: 1.35,
          wordBreak: "break-word",
          fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
        }}
      >
        {label}
      </Typography>
      <FormControl size="small" sx={{ minWidth: { xs: "100%" }, flexShrink: 0, "@media (min-width: 1280px)": { minWidth: 140, width: 140 } }}>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          renderValue={(selected) =>
            selected === "" ? (
              <Typography
                component="span"
                sx={{
                  color: "#8a8370",
                  fontStyle: "italic",
                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                }}
              >
                Select a rank
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "#0B3D6E",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    flexShrink: 0,
                  }}
                >
                  {selected}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    color: "#0B3D6E",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selected === 1
                    ? "Most preferred"
                    : selected === 6
                    ? "Least preferred"
                    : `Rank ${selected}`}
                </Typography>
              </Box>
            )
          }
          sx={{
            backgroundColor: "#fff",
            fontSize: { xs: "0.8rem", md: "0.9rem" },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              py: { xs: 0.5, md: 0.75 },
              pl: 1,
              pr: "32px !important",
              overflow: "hidden",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#D9CBA8",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#B8892B",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#0B3D6E",
              borderWidth: 1.5,
            },
          }}
          MenuProps={{
            PaperProps: { sx: { maxHeight: 340, minWidth: { xs: 200, md: 300 }, maxWidth: "90vw" } },
          }}
        >
          <MenuItem value="">
            <em style={{ color: "#8a8370" }}>Clear selection</em>
          </MenuItem>
          {rankOptions.map((n) => {
            const owner = rankOwner[n];
            const isSelf = owner === itemLabel;
            const isTaken = !!owner && !isSelf;
            return (
              <MenuItem
                key={n}
                value={n}
                disabled={isTaken}
                sx={{
                  alignItems: "center",
                  py: 0.75,
                  "&.Mui-disabled": { opacity: 1 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                    opacity: isTaken ? 0.55 : 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: isTaken ? "#EDE7DA" : "#EAF0F7",
                      color: isTaken ? "#8a8370" : "#0B3D6E",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                    }}
                  >
                    {n}
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.85rem",
                      flex: 1,
                      color: isTaken ? "#8a8370" : "inherit",
                    }}
                  >
                    {n === 1
                      ? "Most preferred"
                      : n === 6
                      ? "Least preferred"
                      : `Rank ${n}`}
                  </Typography>
                  {isTaken && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.7rem",
                        color: "#8a8370",
                        fontStyle: "italic",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Used
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SurveyForm;
